import { memo, useCallback, type CSSProperties, type PointerEvent } from 'react'
import { useSitePhoto } from '../../hooks/useSitePhoto'
import { useMapContext } from '../../context/MapContext'
import { speakSiteInfo, cancelSiteAudio } from '../../utils/audioUtils'
import { MARKER_CONFIG } from '../../constants/markerConfig'
import { getSpokenDescription, type Site } from '../../types'
import { spokenLabel } from '../../constants/mapConfig'
import SpokenAT from '../SpokenAT'

/** Shared by the photo and the colored fallback block, so a card's layout doesn't shift when a
 *  photo fails to load. 16:9 to match the source photos — see the comment at the usage site. */
const thumbStyle: CSSProperties = { width: 88, height: 50, borderRadius: 5, flexShrink: 0 }

interface SiteListCardProps {
  site: Site
  isSelected: boolean
  onClick: (site: Site) => void
}

function SiteListCard({ site, isSelected, onClick }: SiteListCardProps) {
  const { src, hasFallback, onError } = useSitePhoto(site.id)
  const { audioGuide } = useMapContext()
  const config = MARKER_CONFIG[site.category]
  const familyData = site.audiences.family
  const description = getSpokenDescription(site.audiences)
  const handleClick = useCallback(() => onClick(site), [onClick, site])

  // Mirrors SiteMarker's hover-to-preview behavior on the map — without this, browsing the list
  // with the audio guide on says nothing until a card is actually clicked (which narrates via
  // SitePanel), unlike the map where sweeping over markers speaks immediately. Real mouse hover
  // only, same as SiteMarker: keyboard focus already gets this info from aria-label via the
  // user's own screen reader (speaking here too would double it up), and checking pointerType
  // (not just "was this a mouseenter") excludes iOS VoiceOver's touch-explore gesture, which
  // fires a synthetic mouseenter and would otherwise talk over VoiceOver's own speech.
  const handleMouseEnter = useCallback((e: PointerEvent<HTMLButtonElement>) => {
    if (audioGuide && e.pointerType === 'mouse') speakSiteInfo(site, description, () => {})
  }, [audioGuide, site, description])

  // Scoped to this card's own id — same reason as SiteMarker: adjacent list rows sit flush against
  // each other, so the next row's pointerenter can land before this row's leave.
  const handleMouseLeave = useCallback(() => {
    cancelSiteAudio(site.id)
  }, [site.id])

  return (
    <button
      type="button"
      data-testid="site-list-card"
      onClick={handleClick}
      onPointerEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      // "Current item in a set" fits better than aria-pressed: clicking an already-selected card
      // doesn't toggle it back off (aria-pressed implies a two-way toggle), it stays selected.
      aria-current={isSelected ? 'true' : undefined}
      // Name mirrors what's visually on the card (org + city), not just the headline — matches
      // SiteMarker's map-view equivalent, which includes more than the bare site name too.
      // spokenLabel expands the visible "AT" abbreviation for the announcement only — see
      // SiteMarker's equivalent aria-label.
      aria-label={spokenLabel([site.name, [site.org, site.city].filter(Boolean).join(', ')].filter(Boolean).join(' — '))}
      style={{
        all: 'unset',
        display: 'flex',
        width: '100%',
        boxSizing: 'border-box',
        padding: '10px 14px',
        gap: 10,
        background: isSelected ? '#f0f5ff' : '#fff',
        cursor: 'pointer',
        borderBottom: '1px solid #f1f5f9',
        borderLeft: isSelected ? '3px solid #004090' : '3px solid transparent',
      }}
    >
      {/* Photo thumbnail. 16:9 rather than the old 72×54 (4:3): the site photos are all landscape
          16:9-ish, so a 4:3 box plus `object-fit: cover` threw away the left and right thirds of
          every frame — usually the part with the people or equipment in it. Matching the source
          aspect means cover has (near enough) nothing to crop. */}
      {hasFallback ? (
        <div
          data-testid="card-photo-fallback"
          style={{ ...thumbStyle, background: config.color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <span style={{ color: '#fff', fontSize: '0.6rem', fontWeight: 700, textAlign: 'center', padding: '0 4px' }}>
            {config.label}
          </span>
        </div>
      ) : (
        <img
          src={src}
          onError={onError}
          alt=""
          style={{ ...thumbStyle, objectFit: 'cover', objectPosition: 'center' }}
        />
      )}

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <span style={{ fontWeight: 700, fontSize: '0.85rem', color: 'var(--text-strong)', lineHeight: 1.2, minWidth: 0, overflowWrap: 'break-word' }}>
          <SpokenAT text={site.name} />
        </span>
        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 2 }}>
          {site.org} · {site.city}
        </div>
        {familyData?.headline && (
          <div style={{
            fontSize: '0.75rem',
            color: 'var(--text-body)',
            marginTop: 4,
            lineHeight: 1.4,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}>
            <SpokenAT text={familyData.headline} />
          </div>
        )}
      </div>
    </button>
  )
}

export default memo(SiteListCard)
